import { useEffect, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  EmptySectionCard,
  MaudSectionHeader,
  SkeletonLoader,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';

import { PatientEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGetPatientContactsQuery } from '~/store/services/patients';

import ContactList from './ContactList';
import PatientAddContactV2 from '../contactV2/PatientContactV2';

interface PatientContactsProps {
  patientId: string;
  hasPatientDashboardPermission: boolean;
  patientBasicDetails: Patient;
}

const TOP_LEVEL_ID = 'patient-contacts';

const PatientContacts = ({
  patientId,
  hasPatientDashboardPermission,
  patientBasicDetails,
}: PatientContactsProps) => {
  const [isContactInfoAddMode, setIsContactInfoAddMode] =
    useState<boolean>(false);

  const { trackEvent } = useAnalytics();
  const {
    data: patientContactList,
    isError: isGetPatientContactListError,
    error: getPatientContactListError,
    isFetching: isGetpatientContatListFetching,
  } = useGetPatientContactsQuery(patientId, { skip: !patientId });

  useEffect(() => {
    if (isGetPatientContactListError && getPatientContactListError) {
      console.error(
        'Error getting patient contact info',
        getPatientContactListError,
      );
    }
  }, [isGetPatientContactListError, getPatientContactListError]);

  const handleOnAdd = () => {
    setIsContactInfoAddMode(false);
    document
      .getElementById(`${TOP_LEVEL_ID}__section-wrapper`)
      ?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      });
  };

  const supportEmailAddresses = useMemo<string[]>(
    () =>
      (patientContactList ?? [])
        .map((patient) => (patient?.isSupport ? patient?.email : null))
        .filter((email) => Boolean(email)),
    [patientContactList],
  );

  const handleHeaderClick = () => {
    trackEvent(PatientEvents.addContactProfileTabButtonClicked, {
      Source: AnalyticsSourceValues.PATIENT_TAB,
    });
    if (hasPatientDashboardPermission) {
      setIsContactInfoAddMode(true);
    }
  };

  const headerTitle = hasPatientDashboardPermission ? 'Add contact' : undefined;

  return (
    <>
      <ChakraFlex
        flexDirection="column"
        id={`${TOP_LEVEL_ID}__section-wrapper`}
        marginBottom="25px"
        marginTop="50px"
        width="100%"
      >
        <MaudSectionHeader
          count={patientContactList?.length}
          id={`${TOP_LEVEL_ID}__section`}
          onAdd={handleHeaderClick}
          onAddTitle={headerTitle}
          style="secondary"
          title="Contacts"
        />
      </ChakraFlex>
      {hasPatientDashboardPermission && isContactInfoAddMode && (
        <ChakraBox
          borderColor="neutral.line"
          borderRadius="16px"
          borderStyle="solid"
          borderWidth="1px"
          marginY="5px"
          padding="22px 24px"
        >
          <PatientAddContactV2
            onAdd={handleOnAdd}
            onDiscard={() => setIsContactInfoAddMode(false)}
            patientBasicDetails={patientBasicDetails}
            patientId={patientId}
            supportEmailAddresses={supportEmailAddresses}
          />
        </ChakraBox>
      )}

      <SkeletonLoader
        id={TOP_LEVEL_ID}
        isLoaded={!isGetpatientContatListFetching}
      >
        {isEmpty(patientContactList) ? (
          <EmptySectionCard
            caption="There are no contacts associated with this patient"
            id={TOP_LEVEL_ID}
          />
        ) : (
          <ContactList
            contacts={patientContactList}
            hasPatientDashboardPermission={hasPatientDashboardPermission}
            onAdd={handleOnAdd}
            patientBasicDetails={patientBasicDetails}
            patientId={patientId}
            supportEmailAddresses={supportEmailAddresses}
          />
        )}
      </SkeletonLoader>
    </>
  );
};

export default PatientContacts;
